import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import stringifyNumber from 'utils/stringifyNumber';
import typeOfCurrency from 'utils/typeOfCurrency';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import { cityIn } from 'lvovich';
import getEnvironDomain from 'utils/getEnvironDomain';

import texts from 'constants/texts';
import logo from 'icons/img/logo.png';

const TruckCardPageHelmet = observer(() => {
  const location = useLocation();

  const {
    advertsStore: {
      currentAdvert: {
        advert_item,
        advert_items,
        city_verbose,
        price,
        currency,
        period_type,
        advert_type,
        photo_url,
        proposal_type,
      },
      advertLoaded,
      getTruckName,
    },
    dictionariesStore: { engine_types, period_types },
  } = useStores();

  const isPurchase = advert_type === 'purchase';
  const url = location.pathname;
  const city = city_verbose?.split(',')[0];
  const truckDescription = !isPurchase ? texts.meta.callToAction : '';
  const to = isPurchase ? 'до' : '';
  const from = isPurchase ? 'от' : '';
  const atisu = isPurchase ? 'на ATI.SU' : '';

  const actionNames: { [index: string]: string } = {
    sale: 'купить',
    rent: 'арендовать',
  };

  const proposalActionNames: { [index: string]: string } = {
    purchase: 'продать',
    rent: 'сдать в аренду',
  };

  const proposalActionName = (proposal_type && proposalActionNames[proposal_type]) || '';

  const getActionName = (): string => {
    if (proposal_type) return proposalActionName;
    return actionNames[advert_type];
  };

  const renderEngine = () => {
    let engine;
    if (advert_items) {
      const { transport } = advert_items[0];
      engine = [
        transport.engine_volume && `${transport.engine_volume} л`,
        transport.engine_power && `${transport.engine_power} л.с.`,
        transport.engine_type && `${engine_types[transport.engine_type]}`,
      ]
        .filter((el) => el)
        .join(', ');
    } else {
      engine = [
        advert_item?.engine_volume && `${advert_item.engine_volume} л`,
        advert_item?.engine_power && `${advert_item.engine_power} л.с.`,
        advert_item?.engine_type && `${engine_types?.[advert_item.engine_type]}`,
      ]
        .filter((el) => el)
        .join(', ');
    }

    return engine ? `двигатель - ${engine}` : '';
  };

  const renderMileage = () => {
    if (advert_items && advert_items[0].transport?.mileage !== undefined) {
      return `Пробег ${advert_items[0].transport.mileage} км.`;
    }

    if (advert_item?.mileage !== undefined) {
      return `Пробег - ${to} ${advert_item.mileage} км.`;
    }

    if (advert_item?.working_hours !== undefined) {
      return `Моточасы - ${advert_item?.working_hours}`;
    }

    return '';
  };

  const renderYear = () => {
    const truckYear = advert_items ? advert_items[0].transport.year : advert_item?.year;
    return `год - ${from} ${truckYear} ${atisu}`.trim();
  };

  const renderPrice = () => {
    return `по цене ${to} ${stringifyNumber(price)}${typeOfCurrency[currency]}${
      advert_type === 'rent' ? `/${period_types[period_type]}` : ''
    }`;
  };

  const renderTypeName = isPurchase
    ? `${capitalize(proposalActionName)} ${getTruckName()}`
    : getTruckName();

  const renderDescription = () => {
    const firstPart = `${renderTypeName} ${renderPrice()}`;
    const millageAndYearAndEngine = [renderMileage(), renderYear(), renderEngine()]
      .filter(Boolean)
      .join(', ');
    const secondPart = millageAndYearAndEngine;
    const thirdPart = `${truckDescription}`;
    return `${[firstPart, secondPart, thirdPart].filter(Boolean).join('. ')}.`.replace(
      // Замена нескольких точек подряд на одну
      /(?=(\.))\1{2,}/g,
      '.',
    );
  };

  const schemaConfig =
    advertLoaded &&
    `
  {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": ${JSON.stringify(getTruckName())},
    "brand": ${JSON.stringify(
      advert_item?.brand?.name || advert_items?.[0]?.transport?.brand?.name,
    )},
    "logo": "https://files.${getEnvironDomain()}/Images/ati-logo-mobile-en.svg",
    "image": ${JSON.stringify(
      `${
        (isPurchase && !isEmpty(advert_items)) || isPurchase
          ? logo
          : `https://tm.${getEnvironDomain()}${photo_url}`
      }`,
    )},
    "description": ${JSON.stringify(`${renderDescription()}`)},
    "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4,9",
    "reviewCount": "795"
    },
    "offers": {
        "@type": "Offer",
        "url": "https://tm.${getEnvironDomain()}${url}",
        "priceCurrency": "RUB",
        "price": ${JSON.stringify(stringifyNumber(price))},
      }
  }
`;

  const ogImage =
    (isPurchase && !isEmpty(advert_items)) || isPurchase
      ? logo
      : `https://tm.${getEnvironDomain()}${photo_url}`;

  const renderHelmet = () => (
    <Helmet>
      <title>{`${getTruckName()} - ${getActionName()} в ${cityIn(city)}`}</title>
      <meta name="description" content={renderDescription()} />
      <meta
        property="og:title"
        content={`${getTruckName()} - ${getActionName()} в ${cityIn(city)}`}
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://tm.${getEnvironDomain()}${url}`} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={texts.meta.title} />
      <meta property="og:description" content={renderDescription()} />
      <script type="application/ld+json">{schemaConfig}</script>
      <script src="https://yastatic.net/es5-shims/0.0.2/es5-shims.min.js" />
      <script src="https://yastatic.net/share2/share.js" />
    </Helmet>
  );

  return renderHelmet();
});

export default TruckCardPageHelmet;
